import { useMemo } from 'react';
import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ProjectStatusBadge } from './ProjectStatusBadge';
import { needsImmediateAction } from '../lib/flags';
import { groupProjectsByMonth, type ProjectMonthGroup } from '../lib/months';
import { mergeDayRows, type DayRow } from '../lib/tableRows';
import { waitingOf } from '../lib/timeline';
import { EmptyState } from '@/shared/components/EmptyState';
import { buttonVariants } from '@/shared/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { messages } from '@/shared/constants/messages';
import { PROJECT_STATUS_LABELS } from '@/shared/constants/statuses';
import { formatDayParts, formatTime, isSameZonedDay } from '@/shared/lib/dates';
import { formatWaitingDays, shortName } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import type { AgendaOccurrence, ProjectRowView } from '@/shared/types/domain';

/**
 * A ÚNICA forma de listar projeto no painel.
 *
 * Antes eram duas — cartões numa linha do tempo em `/projetos`, cartões-linha
 * agrupados por situação no Início e nas telas de estrutura. Dois vocabulários
 * visuais para a mesma entidade obrigam quem usa o sistema a reaprender a ler a
 * cada tela, e nenhum dos dois respondia bem à pergunta que a liderança faz o
 * tempo todo: comparar projetos entre si. Coluna alinhada compara; cartão, não.
 *
 * **A ordem das colunas é a ordem da leitura, e ela foi pedida assim:** quando
 * acontece, em que pé está, o que é. As três primeiras são as que sobrevivem a
 * 375px; ministério, responsável e tempo de espera entram conforme a tela dá
 * espaço (`hidden sm:table-cell` / `hidden lg:table-cell`), porque nenhuma
 * delas é necessária para decidir se vale abrir o projeto.
 *
 * **A descrição do projeto não entra**, e não é falta de espaço: numa lista ela
 * empurra as linhas para longe umas das outras e desmancha exatamente a
 * varredura vertical que a tabela existe para permitir. Ela mora na tela do
 * projeto.
 *
 * **Passou a listar também o que NÃO é projeto** (spec-calendar.md §5.1): a
 * lista do dia do Calendário recebe as ocorrências de agenda fixa pela prop
 * `occurrences`, interpoladas por horário. A tabela deixou de ser "a lista de
 * projetos" e passou a ser **a lista do que ocupa aquele recorte** — sem deixar
 * de ser a única forma de listar projeto, que era o que a regra protegia.
 *
 * **Não é um kanban por situação, e continua não sendo.** Quatro motivos, cada
 * um bastaria: (a) quem olha esta lista não move os cartões — a esteira é
 * trabalho de `admin`/`overseer`, com fila própria em `/analise` (ADR-012);
 * (b) arrastar mentiria sobre a transição, que só existe por RPC e exige
 * comentário em `reject`/`request_changes` (regra 2); (c) colunas por situação
 * destroem o eixo de data — "o que eu tenho em setembro?" não se responde
 * varrendo seis colunas; (d) a 375px seriam seis rolagens lado a lado (RNF-U02).
 */
interface ProjectTableProps {
  /**
   * ⚠️ **`ProjectRowView`, e não `ProjectWithContext`** (spec-calendar.md §5.1).
   * A fonte "Projetos" do calendário vem da RPC de projeção reduzida, que não
   * carrega `version`, `created_by` nem `branch_id` — a lista de colunas dela é
   * uma decisão de privacidade. Pedir o tipo largo obrigaria quem chama a
   * fabricar quatro colunas para satisfazer o compilador. `ProjectWithContext`
   * satisfaz este tipo estruturalmente: os quatro chamadores anteriores não
   * mudaram uma linha.
   */
  projects: readonly ProjectRowView[];
  /**
   * Ocorrências de agenda fixa, **interpoladas por horário entre os projetos**.
   * Só o Calendário passa — os outros quatro chamadores não sabem que existe, e
   * para eles nada muda.
   *
   * Ocorrência **não é projeto**: a coluna Situação recebe a marca "Agenda
   * fixa", Responsável e Tempo de espera ficam vazios, a linha **não é
   * clicável** (não há tela de detalhe para ela) e não recebe filete de
   * urgência. `ProjectTable` continua sendo a única forma de listar projeto no
   * painel; ela passou a **também** carregar o que ocupa o mesmo dia — porque
   * omitir o culto de um domingo faria a lista mentir sobre o dia que a grade
   * logo acima mostra.
   *
   * Não combina com `groupByMonth`: agrupar por mês é do acervo de `/projetos`,
   * e a lista do dia do calendário é de um dia só.
   */
  occurrences?: readonly AgendaOccurrence[];
  /**
   * Um cabeçalho de mês por bloco. `/projetos` usa; as telas de escopo pequeno
   * (Início, coordenação, ministério) não — ali a lista é curta e o cabeçalho
   * só acrescentaria linha sem acrescentar orientação.
   */
  groupByMonth?: boolean;
  /** Dentro da tela de um ministério a coluna repete o título da página. */
  showMinistry?: boolean;
  /** A fila `/analise` precisa dizer de qual COORDENAÇÃO, não só ministério. */
  showCoordination?: boolean;
  /**
   * De onde sai o filete âmbar da primeira coluna (spec-project-review.md
   * §6.3). `'immediateAction'` (padrão) é o que `/projetos` sempre usou;
   * `'waiting'` é exclusivo da fila de análise — dias de espera viram cor só
   * ali, nunca nas outras telas que reusam esta tabela.
   */
  urgencyBy?: 'immediateAction' | 'waiting';
  /**
   * A coluna "Situação" (decisão 15). Padrão `true` — nenhuma tela existente
   * muda. **A fila `/analise` passa `false`**: ela é, por definição, uma lista
   * de projetos aguardando decisão, e uma coluna que diz "Enviado" em todas as
   * linhas não é informação, é uma coluna a menos para o título.
   *
   * A situação continua chegando a quem usa leitor de tela, como `sr-only` na
   * célula de título — do mesmo jeito que o rótulo de tempo de espera já faz.
   */
  showStatus?: boolean;
  /**
   * A última coluna com o verbo da linha, **exclusiva da fila `/analise`**.
   *
   * Ela não acrescenta um caminho: ela **substitui** os dois que existiam. Com
   * `rowAction`, a linha deixa de ser clicável e o título deixa de ser link —
   * sobra um alvo por linha, e ele é explícito. Numa tela cujo trabalho é
   * decidir, abrir um projeto tem de ser um ato escolhido: o clique largo da
   * linha existe para varrer uma lista, e varrer com o dedo apoiado abria
   * projeto sem querer.
   *
   * Continua sendo um `<Link>` por linha — o mesmo que a célula de título era
   * (teclado, botão do meio, "abrir em nova aba"), vestido de botão.
   */
  rowAction?: boolean;
  /**
   * A rota de ORIGEM, quando a lista pertence a um percurso (decisão 16). A
   * fila `/analise` passa a sua; abrir um projeto por ali mantém "Análise"
   * acesa e a trilha `Análise › Título`.
   *
   * Viaja em `location.state`, nunca na URL — o permalink do projeto é o mesmo
   * em todas as telas. É **enfeite de navegação**: morre no F5 e nada que
   * dependa de autorização ou de estado pode lê-la.
   */
  from?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
}

export function ProjectTable({
  projects,
  occurrences,
  groupByMonth = false,
  showMinistry = true,
  showCoordination = false,
  urgencyBy = 'immediateAction',
  showStatus = true,
  rowAction = false,
  from,
  emptyTitle = 'Nenhum projeto por aqui',
  emptyDescription,
  className,
}: ProjectTableProps) {
  const groups = useMemo<readonly ProjectMonthGroup<DayRow>[]>(
    () =>
      groupByMonth
        ? groupProjectsByMonth(projects).map((group) => ({
            ...group,
            items: group.items.map((project): DayRow => ({ kind: 'project', project })),
          }))
        : [{ key: 'todos', label: '', items: mergeDayRows(projects, occurrences ?? []) }],
    [groupByMonth, occurrences, projects],
  );

  // A coluna só existe se alguma linha tiver o que pôr nela. Um cabeçalho
  // "Tempo de espera" sobre seis células vazias afirma que nada está parado de
  // um jeito que parece defeito — e rouba largura de quem tem o que dizer.
  const showWaiting = useMemo(
    () => projects.some((project) => waitingOf(project) !== null),
    [projects],
  );

  if (projects.length === 0 && (occurrences?.length ?? 0) === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <ProjectTableFrame
      groups={groups}
      showMinistry={showMinistry}
      showCoordination={showCoordination}
      showWaiting={showWaiting}
      showMonthLabels={groupByMonth}
      urgencyBy={urgencyBy}
      showStatus={showStatus}
      rowAction={rowAction}
      from={from}
      className={className}
    />
  );
}

interface ProjectTableFrameProps {
  groups: readonly ProjectMonthGroup<DayRow>[];
  showMinistry: boolean;
  showCoordination: boolean;
  showWaiting: boolean;
  showMonthLabels: boolean;
  urgencyBy: 'immediateAction' | 'waiting';
  showStatus: boolean;
  rowAction: boolean;
  from?: string;
  className?: string;
}

/**
 * Uma `<table>` só, com um `<tbody>` por mês.
 *
 * Uma tabela POR mês seria mais simples de escrever e está errada: cada uma
 * mediria as próprias colunas, e a de agosto sairia com larguras diferentes da
 * de setembro. O que faz a varredura vertical funcionar é a coluna continuar no
 * mesmo lugar quando o olho atravessa o cabeçalho do mês.
 */
function ProjectTableFrame({
  groups,
  showMinistry,
  showCoordination,
  showWaiting,
  showMonthLabels,
  urgencyBy,
  showStatus,
  rowAction,
  from,
  className,
}: ProjectTableFrameProps) {
  return (
    // `table-fixed`: a largura de cada coluna passa a valer exatamente o que o
    // cabeçalho declara, em vez de o navegador negociá-la com o conteúdo. É o
    // que faz `truncate` funcionar de verdade numa célula — em `table-layout:
    // auto` um nome de ministério longo empurra a coluna e o texto nunca corta.
    <Table className={cn('table-fixed', className)}>
      {/* Os pontos de corte não são os do Tailwind por acaso — são onde a
          SIDEBAR muda de tamanho. Ela reaparece em `md` e leva 248px consigo,
          de modo que uma coluna revelada ali espremeria o título justamente na
          largura em que a tela ficou mais apertada. Por isso ministério só
          entra em `lg` e as duas últimas em `xl`. */}
      <TableHeader>
        <TableRow>
          <TableHead className="w-[6rem] lg:w-[8.5rem]">Data</TableHead>
          {showStatus ? <TableHead className="w-[7rem] sm:w-[11rem]">Situação</TableHead> : null}
          <TableHead>Título</TableHead>
          {showMinistry ? (
            // `showCoordination` NÃO abre uma coluna nova (o content-width
            // disponível — `max-w-6xl` menos os 248px da sidebar — já fica
            // apertado com Ministério + Ação juntos entre `lg` e `xl`; uma
            // coluna a mais estourava a tabela sem barra de rolagem para
            // avisar, exatamente o que o docblock do Table proíbe). A
            // coordenação vira SEGUNDA LINHA dentro da mesma célula.
            <TableHead className="hidden lg:table-cell lg:w-[10rem]">Ministério</TableHead>
          ) : null}
          <TableHead className="hidden xl:table-cell xl:w-[9rem]">Responsável</TableHead>
          {showWaiting ? (
            // 8,5rem é a largura do RÓTULO, não do conteúdo — a célula abaixo
            // carrega "há 21 dias" e caberia em bem menos. O nome inteiro vale
            // os pixels: "Espera", sozinho, deixa a coluna ambígua entre tempo
            // de espera e o que se espera.
            <TableHead className="hidden text-right xl:table-cell xl:w-[8.5rem]">
              Tempo de espera
            </TableHead>
          ) : null}
          {rowAction ? (
            // A ÚNICA coluna que nunca se retira: a 375px ela é o único jeito
            // de abrir o projeto, e uma coluna que some levando a navegação
            // junto deixaria a fila inutilizável no celular.
            <TableHead className="w-[6.25rem] text-right">
              {/* O rótulo existe para o leitor de tela e só: escrito na tela,
                  "Ação" nomeia o óbvio e rouba a largura do título. */}
              <span className="sr-only">Ação</span>
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>

      {groups.map((group) => (
        /**
         * ⚠️ **Sem `.stagger` aqui, e a ausência custou um defeito para ser
         * entendida.** A cascata de entrada aplica `animation: rise-in` a cada
         * filho direto — ou seja, a cada `<tr>` —, e uma animação de
         * `transform` faz do elemento um contexto de empilhamento. O `z-10` do
         * cabeçalho de mês passava a valer só DENTRO da linha dele, e as
         * linhas seguintes, por serem irmãs posteriores, passavam a ser
         * pintadas POR CIMA da faixa grudada: rolar a tabela mostrava o nome do
         * mês e o nome do projeto ocupando os mesmos pixels.
         *
         * A cascata também não faz falta: ela foi desenhada para lista de
         * cartões, onde cada item é um bloco que chega. Numa tabela a entrada
         * de tela inteira (`animate-page-in`, no `AppShell`) já responde, e 50
         * linhas subindo em sequência competiriam com a leitura em coluna que
         * a tabela existe para permitir.
         */
        <TableBody key={group.key}>
          {showMonthLabels ? (
            <MonthHeaderRow
              label={group.label}
              showMinistry={showMinistry}
              showWaiting={showWaiting}
              showStatus={showStatus}
              rowAction={rowAction}
            />
          ) : null}

          {group.items.map((row) =>
            row.kind === 'project' ? (
              <ProjectRow
                key={`p:${row.project.id}`}
                project={row.project}
                showMinistry={showMinistry}
                showCoordination={showCoordination}
                showWaiting={showWaiting}
                urgencyBy={urgencyBy}
                showStatus={showStatus}
                rowAction={rowAction}
                from={from}
              />
            ) : (
              <OccurrenceRow
                key={`o:${row.occurrence.id}`}
                occurrence={row.occurrence}
                showMinistry={showMinistry}
                showWaiting={showWaiting}
                showStatus={showStatus}
                rowAction={rowAction}
              />
            ),
          )}
        </TableBody>
      ))}
    </Table>
  );
}

/**
 * O cabeçalho do mês: "bem secundário", como pedido — ele organiza, não
 * compete. Quem carrega a leitura é a data da primeira coluna. Gruda logo
 * abaixo do cabeçalho da tabela (`top-14` + `h-9` = 5.75rem), para nunca se
 * saber olhando uma linha de qual mês ela é.
 *
 * ⚠️ **Uma célula por coluna, e NÃO um `colSpan`.** O `colSpan` era o jeito
 * óbvio e quebrava a tabela inteira a 375px: as colunas responsivas somem por
 * `display: none`, mas um `colSpan={6}` continua AFIRMANDO seis colunas, então
 * o navegador criava três colunas fantasma e dava a elas o espaço que devia
 * ser do título — que sobrava com uma letra e meia na tela do celular. Repetir
 * as condições de visibilidade aqui é o preço de a contagem de colunas ser a
 * mesma em toda linha da tabela.
 *
 * O rótulo transborda para a direita por cima das próprias células vazias
 * (`whitespace-nowrap`, e célula de tabela não recorta por padrão): assim ele
 * cabe inteiro mesmo na coluna de data, que tem 6rem.
 */
function MonthHeaderRow({
  label,
  showMinistry,
  showWaiting,
  showStatus,
  rowAction,
}: {
  label: string;
  showMinistry: boolean;
  showWaiting: boolean;
  showStatus: boolean;
  rowAction: boolean;
}) {
  // Opaco pelo mesmo motivo do `TableHead`: as linhas passam por baixo desta
  // faixa, e um fundo translúcido as deixaria legíveis através do nome do mês.
  const cell = 'sticky top-[5.75rem] z-10 border-b border-border bg-card pb-1.5 pt-5';

  return (
    <TableRow>
      <th
        scope="colgroup"
        className={cn(
          cell,
          // Um degrau acima das células vazias ao lado: elas são irmãs
          // POSTERIORES e, com o mesmo `z`, pintariam o próprio fundo por cima
          // do rótulo que transborda sobre elas — o mês aparecia com as duas
          // últimas letras cobertas.
          'z-[11] whitespace-nowrap px-3 text-left text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground/80',
        )}
      >
        {label}
      </th>
      {showStatus ? <td className={cell} /> : null}
      <td className={cell} />
      {showMinistry ? <td className={cn(cell, 'hidden lg:table-cell')} /> : null}
      <td className={cn(cell, 'hidden xl:table-cell')} />
      {showWaiting ? <td className={cn(cell, 'hidden xl:table-cell')} /> : null}
      {rowAction ? <td className={cell} /> : null}
    </TableRow>
  );
}

interface ProjectRowProps {
  project: ProjectRowView;
  showMinistry: boolean;
  showCoordination: boolean;
  showWaiting: boolean;
  urgencyBy: 'immediateAction' | 'waiting';
  showStatus: boolean;
  rowAction: boolean;
  from?: string;
}

function ProjectRow({
  project,
  showMinistry,
  showCoordination,
  showWaiting,
  urgencyBy,
  showStatus,
  rowAction,
  from,
}: ProjectRowProps) {
  const navigate = useNavigate();
  const href = `/projetos/${project.id}`;
  const linkState = from ? { from } : undefined;
  const waiting = waitingOf(project);
  // Só a fila usa `waiting?.alert` (spec §6.3) — em todo o resto do painel a
  // cor do filete continua vindo de `needsImmediateAction`.
  const urgent = urgencyBy === 'waiting' ? Boolean(waiting?.alert) : needsImmediateAction(project);

  /**
   * A linha inteira é clicável, mas o LINK é um só, na célula de título.
   *
   * ⚠️ **Nada disto vale sob `rowAction`**: ali o único alvo é o botão da
   * última coluna, e o `onClick` nem é registrado. Uma linha que navega por
   * baixo de um botão que também navega dá dois caminhos para o mesmo lugar e
   * um deles é invisível.
   *
   * Envolver cada célula numa âncora daria seis links por projeto ao leitor de
   * tela e seis paradas de `Tab` — o custo cairia justamente sobre quem menos
   * pode pagá-lo. Com uma âncora real no título, o teclado e a tecnologia
   * assistiva enxergam uma tabela com um link por linha, que é a verdade; o
   * `onClick` aqui é conveniência de mouse por cima disso.
   */
  function handleRowClick(event: MouseEvent<HTMLTableRowElement>) {
    if (event.button !== 0 || event.defaultPrevented) return;

    // Clique que nasceu num elemento interativo é dele, não da linha — inclui a
    // própria âncora do título, que navega sozinha.
    if ((event.target as HTMLElement).closest('a, button, input, select, textarea, label')) {
      return;
    }

    // Arrastar para selecionar o nome de um ministério terminava em navegação:
    // o `mouseup` fora do texto ainda conta como clique na linha.
    if (window.getSelection()?.toString()) return;

    // "Abrir em nova aba" tem de valer para a linha toda, e não só para as
    // poucas dezenas de pixels do título.
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(href, { state: linkState });
  }

  return (
    <TableRow
      onClick={rowAction ? undefined : handleRowClick}
      className={cn(
        'group',
        // Sem o verbo por linha, a linha É o botão: ela avisa disso com o
        // cursor e com o realce. Com ele, o mesmo realce prometeria um clique
        // que a linha não tem mais.
        !rowAction && 'cursor-pointer',
        // `transition-colors` sem a utilitária `duration-*`: o plugin
        // `tailwindcss-animate` emite `duration` como transition-duration E
        // animation-duration, e um `duration-fast` aqui encurtaria a entrada da
        // `.stagger` para 110ms (ver tailwind.config.ts).
        'transition-colors [transition-duration:110ms]',
        // `:active` responde no pointer-down, e não na soltura — é a diferença
        // entre a linha parecer viva e parecer que engasgou. Nada de `scale`
        // numa `<tr>`: deformaria a grade da tabela inteira.
        !rowAction && 'hover:bg-accent/50 active:bg-accent',
      )}
    >
      <DateCell project={project} urgent={urgent} />

      {showStatus ? (
        <TableCell>
          <ProjectStatusBadge project={project} />
        </TableCell>
      ) : null}

      <TableCell>
        {/* Sob `rowAction` o título é TEXTO, não link: dois links por linha
            para o mesmo destino dobram as paradas de `Tab` e desmentem o
            botão, que a coluna ao lado apresenta como o caminho. */}
        {rowAction ? (
          <span className="block truncate font-medium text-foreground">
            <StatusForScreenReader project={project} showStatus={showStatus} />
            {project.title ?? <span className="text-muted-foreground">Sem título</span>}
          </span>
        ) : (
          <Link
            to={href}
            state={linkState}
            className="block truncate font-medium text-foreground outline-none group-hover:underline focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-2"
          >
            <StatusForScreenReader project={project} showStatus={showStatus} />
            {project.title ?? <span className="text-muted-foreground">Sem título</span>}
          </Link>
        )}
      </TableCell>

      {showMinistry ? (
        <TableCell className="hidden text-muted-foreground lg:table-cell">
          <span className="block truncate">{project.ministry_name}</span>
          {/* Segunda linha, não coluna nova (§ largura acima). */}
          {showCoordination ? (
            <span className="block truncate text-xs text-muted-foreground/70">
              {project.coordination_name}
            </span>
          ) : null}
        </TableCell>
      ) : null}

      <TableCell className="hidden text-muted-foreground xl:table-cell">
        <span className="block truncate">
          {project.responsible_name ? (
            shortName(project.responsible_name)
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </span>
      </TableCell>

      {showWaiting ? (
        /**
         * Só a DURAÇÃO, e não a frase inteira.
         *
         * `waitingOf` devolve também um rótulo — "Aguardando decisão",
         * "Aguardando ajuste" —, e ele saiu da célula porque a coluna ao lado
         * já o diz: quem está "Enviado" aguarda decisão, quem está com
         * "Ajustes solicitados" aguarda ajuste. Escrever os dois ocupava um
         * terço da tabela para repetir a mesma informação, e foi o que fazia a
         * última coluna vazar para fora da área de conteúdo.
         *
         * O rótulo continua chegando a quem precisa dele: no `title` para o
         * mouse e como texto visível ao leitor de tela, que lê a célula fora
         * do contexto da linha e não teria como derivá-lo.
         */
        <TableCell className="hidden whitespace-nowrap text-right xl:table-cell">
          {waiting ? (
            <span className="tabular text-muted-foreground" title={waiting.label}>
              <span className="sr-only">{waiting.label}, </span>
              {formatWaitingDays(waiting.days)}
            </span>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </TableCell>
      ) : null}

      {rowAction ? (
        <TableCell className="text-right">
          {/* `<Link>` vestido de botão, e não um `<button>` com `navigate()`:
              abrir em nova aba, clicar com o meio e ver o endereço na barra de
              status são coisas que a fila usa de verdade — o revisor abre três
              projetos lado a lado (ver o docblock de `buttonVariants`). */}
          <Link
            to={href}
            state={linkState}
            aria-label={messages.review.queueRowActionFor(project.title)}
            // `size: 'sm'` pela ALTURA (36px de alvo no toque), `px-0` para o
            // verbo encostar na borda direita da coluna como o resto do
            // conteúdo alinhado à direita — o padding do botão o deslocaria
            // 12px para dentro e desalinharia a coluna inteira.
            className={buttonVariants({ variant: 'link', size: 'sm', className: 'px-0' })}
          >
            {messages.review.queueRowAction}
          </Link>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

/**
 * Sem a coluna de Situação, a situação vai por aqui a quem lê a tabela por
 * leitor de tela: ela some da TELA, não do documento.
 */
function StatusForScreenReader({
  project,
  showStatus,
}: {
  project: ProjectRowView;
  showStatus: boolean;
}) {
  if (showStatus) return null;
  return <span className="sr-only">{PROJECT_STATUS_LABELS[project.status]}, </span>;
}

/**
 * A linha de uma ocorrência de agenda fixa (spec-calendar.md §5.1).
 *
 * **Ela não é clicável, e a ausência do link é a informação:** não existe tela
 * de detalhe para uma ocorrência (spec-fixed-agenda.md §7), e uma linha que
 * parece abrir e não abre é pior do que uma linha que nunca prometeu abrir.
 * Sem `cursor-pointer`, sem `hover` de linha clicável, sem filete de urgência —
 * agenda fixa nunca pede ação de ninguém: ela acontece.
 *
 * A coluna Situação carrega a marca "Agenda fixa" no lugar do crachá. Ela é o
 * que impede a leitura errada mais provável da lista do dia: culto de domingo
 * lido como um projeto aprovado que alguém precisa acompanhar.
 */
function OccurrenceRow({
  occurrence,
  showMinistry,
  showWaiting,
  showStatus,
  rowAction,
}: {
  occurrence: AgendaOccurrence;
  showMinistry: boolean;
  showWaiting: boolean;
  showStatus: boolean;
  rowAction: boolean;
}) {
  return (
    <TableRow>
      {/* A borda transparente é a mesma de uma linha de projeto não urgente:
          sem ela o conteúdo ficaria 2px à esquerda das vizinhas. */}
      <TableCell className="border-l-2 border-l-transparent">
        <span className="tabular font-medium">{formatTime(occurrence.starts_at)}</span>
      </TableCell>

      {showStatus ? (
        <TableCell>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-slate-400" />
            Agenda fixa
          </span>
        </TableCell>
      ) : null}

      <TableCell>
        <span className="block truncate font-medium text-foreground">
          {!showStatus ? <span className="sr-only">Agenda fixa, </span> : null}
          {occurrence.title}
        </span>
        {occurrence.space_names.length > 0 ? (
          <span className="block truncate text-xs text-muted-foreground">
            {occurrence.space_names.join(', ')}
          </span>
        ) : null}
      </TableCell>

      {/* Ocorrência não tem ministério dono, responsável nem espera. As células
          existem porque a contagem de colunas tem de ser a mesma em toda linha
          da tabela — mesma razão do cabeçalho de mês não usar `colSpan`. */}
      {showMinistry ? (
        <TableCell className="hidden text-muted-foreground/60 lg:table-cell">—</TableCell>
      ) : null}
      <TableCell className="hidden text-muted-foreground/60 xl:table-cell">—</TableCell>
      {showWaiting ? <TableCell className="hidden xl:table-cell" /> : null}
      {/* Célula vazia, e a ausência do verbo é a informação: não há o que
          analisar numa ocorrência de agenda fixa. */}
      {rowAction ? <TableCell /> : null}
    </TableRow>
  );
}

/**
 * A data, que é a primeira coisa que se lê e a única que se varre.
 *
 * Dia em `tabular` para os números ficarem em coluna; o dia da semana apagado
 * ao lado, porque quem planeja evento de igreja pensa em sábado e domingo antes
 * de pensar em número. Projeto de vários dias mostra o intervalo (`05–07/set`)
 * — retiro e congresso são caso normal, não exceção.
 *
 * O mês vai junto do dia mesmo sob um cabeçalho que já diz "setembro de 2026":
 * a mesma tabela é usada sem agrupamento no Início e nas telas de estrutura,
 * e uma coluna cujo sentido depende de um cabeçalho que pode não existir é uma
 * coluna que mente em metade das telas.
 */
function DateCell({ project, urgent }: { project: ProjectRowView; urgent: boolean }) {
  const start = project.starts_at;

  return (
    <TableCell
      className={cn(
        // O filete âmbar é o sinal de "alguém precisa sentar e resolver" que
        // era a borda do cartão. Fica na primeira célula porque com
        // `border-separate` uma borda declarada na `<tr>` não pinta. A linha
        // não urgente carrega a mesma borda transparente, senão o conteúdo
        // dela ficaria 2px à esquerda das vizinhas.
        'border-l-2',
        urgent ? 'border-l-amber-400' : 'border-l-transparent',
      )}
    >
      {start ? <DateParts project={project} start={start} /> : (
        <span className="italic text-muted-foreground/70">sem data</span>
      )}
    </TableCell>
  );
}

function DateParts({ project, start }: { project: ProjectRowView; start: string }) {
  const from = formatDayParts(start);
  const multiDay = project.ends_at ? !isSameZonedDay(start, project.ends_at) : false;
  const to = multiDay && project.ends_at ? formatDayParts(project.ends_at) : null;

  return (
    // Sem `gap`: os dois pedaços de um intervalo formam UMA data, e um espaço
    // entre eles vira `31/jul– 14/ago`, que se lê como duas coisas. O respiro
    // antes do dia da semana é dele, e vai na margem dele.
    <span className="flex flex-wrap items-baseline">
      {/* Cada pedaço é `inline-block` para a quebra acontecer ENTRE eles e
          nunca dentro de uma data: a 375px `20/ago–17/set` não cabe nos 6rem da
          coluna, e quebrar em `20/ago–` / `17/set` é a única forma de mostrar o
          intervalo inteiro sem invadir a coluna vizinha. */}
      {range(from, to).map((part) => (
        <span key={part} className="tabular inline-block whitespace-nowrap font-medium">
          {part}
        </span>
      ))}
      {/* O dia da semana só cabe quando a data é de um dia só; num intervalo
          ele responderia "qual dos três?". E some abaixo de `sm`: ali ele
          empurrava metade das datas para uma segunda linha, e num celular o
          título precisa daqueles pixels mais do que o "sáb" precisa aparecer
          antes de abrir o projeto. */}
      {to ? null : (
        <span className="ml-1.5 hidden text-xs text-muted-foreground/70 sm:inline">
          {from.weekday}
        </span>
      )}
    </span>
  );
}

type DayParts = ReturnType<typeof formatDayParts>;

/**
 * `05/set` · `24–26/ago` · `20/ago–` + `17/set`
 *
 * ⚠️ A terceira forma existe porque a segunda, sozinha, MENTIA. Fatorar o mês
 * para fora (`24–26/ago`) só é legítimo quando ele é o mesmo dos dois lados;
 * aplicado a um projeto que começa em agosto e termina em setembro, o
 * resultado era `20–17/set` — uma data que termina antes de começar, no mês
 * errado. Congresso e campanha atravessando o mês são caso comum aqui, não
 * exceção.
 *
 * Devolve pedaços, e não uma frase pronta, porque só o intervalo entre meses
 * pode quebrar em duas linhas — e ele tem de quebrar no lugar certo.
 */
function range(from: DayParts, to: DayParts | null): readonly string[] {
  if (!to) return [`${from.day}/${from.month}`];
  return from.month === to.month
    ? [`${from.day}–${to.day}/${to.month}`]
    : [`${from.day}/${from.month}–`, `${to.day}/${to.month}`];
}
